import { useFormikContext, getIn } from 'formik';
import PropTypes from 'prop-types';
import { useCallback } from 'react';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';

import Select from '../Select';
import SelectArray from '../SelectArray';

const DoubleSelect = ({
  childLabel,
  childMultiple = false,
  disabled,
  inputHtml,
  isClearable = false,
  label,
  labelMultiline = true,
  name,
  options,
  placeholder,
  tooltip,
  validate = {},
  menuShouldBlockScroll,
  wrapperProps = {},
}) => {
  const {
    errors,
    values,
    touched,
    submitCount,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();
  const { required } = validate;
  const rootName = `${name}[root_value]`;
  const rootId = `${name}[root_value]-id`;
  const childName = `${name}[child_value]`;
  const childId = `${name}[child_value]-id`;
  const rootValue = getIn(values, rootName);
  const rootError = getIn(errors, rootName);
  const rootTouched = getIn(touched, rootName);
  const childError = getIn(errors, childName);
  const childTouched = getIn(touched, childName);
  const rootOptions = Object.keys(options);
  const childOptions = options[rootValue];

  const displayRootError = (rootTouched || submitCount > 0) && rootError;
  const displayChildError =
    !displayRootError && (childTouched || submitCount > 0) && childError;

  const handleRootChange = useCallback(() => {
    setFieldValue(childName, []);
    setFieldTouched(childName, false);
  }, [childName, setFieldValue, setFieldTouched]);

  return (
    <Box display="block" mb={6} {...wrapperProps}>
      <Box>
        <Box flex={1} mr={3} display="block">
          <Select
            disabled={disabled}
            hideError
            id={rootId}
            inputHtml={inputHtml}
            isClearable={isClearable}
            label={label}
            labelMultiline={labelMultiline}
            name={rootName}
            onChange={handleRootChange}
            options={rootOptions}
            placeholder={placeholder}
            tooltip={tooltip}
            validate={{ required }}
            wrapperProps={{ mb: 0 }}
            isNotSearchable={rootOptions?.length <= 10}
            menuShouldBlockScroll={menuShouldBlockScroll}
          />
        </Box>

        <Box flex={1} ml={3} display="block">
          {rootValue && (
            <SelectArray
              disabled={disabled}
              hideError
              inputHtml={inputHtml}
              isClearable={isClearable}
              label={childLabel || rootValue}
              labelMultiline={labelMultiline}
              multiple={childMultiple}
              name={childName}
              options={childOptions}
              placeholder={childLabel}
              tooltip={tooltip}
              validate={{ required }}
              wrapperProps={{ mb: 0 }}
              isNotSearchable={childOptions?.length <= 10}
              menuShouldBlockScroll={menuShouldBlockScroll}
            />
          )}
        </Box>
      </Box>
      {displayRootError && (
        <AdditionalInformation type="error" id={rootId}>
          {rootError}
        </AdditionalInformation>
      )}
      {displayChildError && (
        <AdditionalInformation type="error" id={childId}>
          {childError}
        </AdditionalInformation>
      )}
    </Box>
  );
};

DoubleSelect.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.object.isRequired,
  childLabel: PropTypes.string,
  childMultiple: PropTypes.bool,
  disabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  label: PropTypes.string,
  labelMultiline: PropTypes.bool,
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  validate: PropTypes.shape({
    required: PropTypes.bool,
  }),
  wrapperProps: PropTypes.object,
};

export default DoubleSelect;
